import { GridBreak, RichTextReader, Typography } from "@compass/core";
import {
  RadioGroup as MuiRadioGroup,
  RadioGroupProps as MuiRadioGroupProps,
  FormControl,
  FormLabel,
  Radio,
  Grid,
  useMediaQuery,
  Stack,
  Box,
  Checkbox,
} from "@mui/material";
import { FormControlLabel } from "@mui/material";
import { Input } from "components";
import { ApplicationQuestion, FormQuestion } from "generated/openApi";
import { FormQuestionTypes } from "pages/cobro/surveys/SurveyPage/Questions/SurveyQuestionEnum";
import { TransientFormAnswer } from "./TransientQuestionResponse";

export type MatrixGroupProps = MuiRadioGroupProps & {
  formQuestion?: FormQuestion;
  applicationQuestion?: ApplicationQuestion;
  isDisabled: boolean;
  values: TransientFormAnswer[];
  handleChange: (value: TransientFormAnswer, clicked?: boolean) => any;
  subQuestions: MatrixQuestion[];
  options: MatrixOption[];
};

export type MatrixQuestion = {
  label: string;
  question: string;
  subQuestionId: number;
  branchedFromSubQuestionId?: number; // for Double Matrix nonsense
};

export type MatrixOption = {
  label: string;
  formAnswerId: number;
};

export const MatrixGroup = ({
  formQuestion,
  applicationQuestion,
  isDisabled,
  values,
  subQuestions,
  options,
  handleChange,
}: MatrixGroupProps) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  // default question size
  let subQuestionSize: number = 3;

  if (formQuestion && formQuestion.questionWidthPercent != undefined && formQuestion.questionWidthPercent) {
    // % values do not mix well with grid, so using some ranges
    if (formQuestion.questionWidthPercent < 25) subQuestionSize = 3;
    else if (formQuestion.questionWidthPercent >= 25 && formQuestion.questionWidthPercent < 40) subQuestionSize = 4;
    else if (formQuestion.questionWidthPercent >= 40 && formQuestion.questionWidthPercent < 50) subQuestionSize = 5;
    else if (formQuestion.questionWidthPercent >= 50 && formQuestion.questionWidthPercent < 60) subQuestionSize = 6;
    else if (formQuestion.questionWidthPercent >= 60 && formQuestion.questionWidthPercent < 68) subQuestionSize = 7;
    else if (formQuestion.questionWidthPercent >= 68 && formQuestion.questionWidthPercent < 75) subQuestionSize = 8;
    else if (formQuestion.questionWidthPercent >= 75) subQuestionSize = 9;
  }

  const remainingColumns: number = 12 - subQuestionSize;
  const optionSize: number = Math.floor(remainingColumns / options.length);
  const mobileOptionSize: number = Math.floor(12 / options.length);

  const onHandleChange = (optionValue: any, question: MatrixQuestion) => {
    var selectedValue = optionValue.target.value;
    const newValue = getValue(question)?.toString() === selectedValue ? null : selectedValue;

    handleChange({
      subQuestionId: question.subQuestionId,
      formAnswerId: parseInt(newValue),
    });
  };

  const onHandleWriteInChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    question: MatrixQuestion,
    formAnswerId: number
  ) => {
    handleChange({
      subQuestionId: question.subQuestionId,
      formAnswerId: formAnswerId,
      writeIn: event.target.value,
    });
  };

  const handleMultiChange = (event: React.ChangeEvent<HTMLInputElement>, question: MatrixQuestion) => {
    handleChange(
      {
        subQuestionId: question.subQuestionId,
        formAnswerId: parseInt((event.target as HTMLInputElement).value),
      },
      event.target.checked
    );
  };

  const getValue = (question: MatrixQuestion): number | null => {
    const value = values.find((v) => v.subQuestionId == question.subQuestionId);
    if (value) return value?.formAnswerId!;
    return null;
  };

  const renderSurveyDesktopView = () => {
    return (
      <>
        {formQuestion && (
          <>
            <Grid container spacing="md">
              <Grid item xs={subQuestionSize} style={{ marginRight: -2 }}>
                {formQuestion.formQuestionTypeId == FormQuestionTypes.MatrixGrouped && (
                  <RichTextReader html={formQuestion.name} />
                )}
              </Grid>
              <Grid item xs={remainingColumns}>
                <Stack direction="row" spacing="sm">
                  {options.map((o, i) => {
                    return (
                      <div key={i} style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "end" }}>
                        <RichTextReader
                          html={o.label}
                          customStyles={{
                            div: {
                              textAlign: "center",
                              padding: 1,
                            },
                          }}
                        />
                      </div>
                    );
                  })}
                </Stack>
              </Grid>

              <GridBreak />

              <Grid item xs={12}>
                <FormControl style={{ width: "100%" }}>
                  {subQuestions.map((q, i) => {
                    return (
                      <MuiRadioGroup
                        sx={{
                          paddingY: 1,
                          background: i % 2 && formQuestion.isAlternatingRowColor ? "#f5f5f5" : "white",
                        }}
                        row
                        key={i}
                        aria-labelledby="radio-buttons-group"
                        name="controlled-radio-buttons-group"
                        value={getValue(q)}
                        // onClick={(e) => onHandleChange(e, q)}
                      >
                        <Grid item xs={subQuestionSize} style={{ flex: 1, display: "flex", alignItems: "center" }}>
                          <Stack direction="row" spacing={2}>
                            {(formQuestion.formQuestionTypeId == FormQuestionTypes.MatrixGrouped ||
                              !formQuestion.hideSubQuestionLabel) && (
                              <RichTextReader
                                customStyles={{
                                  div: {
                                    textAlign: "center",
                                  },
                                }}
                                html={q.label}
                              />
                            )}
                            <RichTextReader html={q.question} />
                          </Stack>
                        </Grid>
                        <Grid item xs={remainingColumns}>
                          <Stack direction="row" spacing="sm">
                            {options.map((option, index) => (
                              <div key={index} style={{ flex: 1, display: "flex", justifyContent: "center" }}>
                                {(formQuestion.formQuestionTypeId == FormQuestionTypes.Matrix ||
                                  formQuestion.formQuestionTypeId == FormQuestionTypes.MatrixGrouped) && (
                                  <FormControlLabel
                                    sx={{ margin: 0 }}
                                    value={option.formAnswerId}
                                    control={<Radio onClick={(e) => onHandleChange(e, q)} />}
                                    label=""
                                    disabled={isDisabled}
                                  />
                                )}
                                {formQuestion.formQuestionTypeId == FormQuestionTypes.MatrixWriteIn && (
                                  <FormControlLabel
                                    sx={{ margin: 0 }}
                                    disabled={isDisabled}
                                    value={option.formAnswerId}
                                    control={
                                      <Input
                                        multiline
                                        disabled={isDisabled}
                                        value={
                                          values.find(
                                            (v) =>
                                              v.formAnswerId == option.formAnswerId &&
                                              v.subQuestionId == q.subQuestionId
                                          )?.writeIn || ""
                                        }
                                        onChange={(e) => onHandleWriteInChange(e, q, option.formAnswerId)}
                                      />
                                    }
                                    label=""
                                  />
                                )}
                                {formQuestion.formQuestionTypeId == FormQuestionTypes.MatrixMultipleChoice && (
                                  <FormControlLabel
                                    sx={{ margin: 0 }}
                                    disabled={isDisabled}
                                    value={option.formAnswerId}
                                    control={
                                      <Checkbox
                                        checked={values.some(
                                          (v) =>
                                            v.formAnswerId == option.formAnswerId && v.subQuestionId == q.subQuestionId
                                        )}
                                        onChange={(e) => handleMultiChange(e, q)}
                                        name={option.label}
                                        disabled={isDisabled}
                                      />
                                    }
                                    label=""
                                  />
                                )}
                              </div>
                            ))}
                          </Stack>
                        </Grid>
                      </MuiRadioGroup>
                    );
                  })}
                </FormControl>
              </Grid>
            </Grid>
          </>
        )}
      </>
    );
  };

  const renderApplicationDesktopView = () => {
    return (
      <>
        {applicationQuestion && (
          <>
            <Grid container spacing="md">
              <Grid item xs={subQuestionSize} style={{ marginRight: -2 }}>
                {applicationQuestion.formQuestionTypeId == FormQuestionTypes.MatrixGrouped && (
                  <RichTextReader html={applicationQuestion.name} />
                )}
              </Grid>
              <Grid item xs={remainingColumns}>
                <Stack direction="row" spacing="sm">
                  {options.map((o, i) => {
                    return (
                      <div key={i} style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "end" }}>
                        <RichTextReader
                          html={o.label}
                          customStyles={{
                            div: {
                              textAlign: "center",
                              padding: 1,
                            },
                          }}
                        />
                      </div>
                    );
                  })}
                </Stack>
              </Grid>

              <GridBreak />

              <Grid item xs={12}>
                <FormControl style={{ width: "100%" }}>
                  {subQuestions.map((q, i) => {
                    return (
                      <MuiRadioGroup
                        sx={{
                          paddingY: 1,
                          background: "white",
                        }}
                        row
                        key={i}
                        aria-labelledby="radio-buttons-group"
                        name="controlled-radio-buttons-group"
                        value={getValue(q)}
                        // onClick={(e) => onHandleChange(e, q)}
                      >
                        <Grid item xs={subQuestionSize} style={{ flex: 1, display: "flex", alignItems: "center" }}>
                          <Stack direction="row" spacing={2}>
                            {applicationQuestion.formQuestionTypeId == FormQuestionTypes.MatrixGrouped && (
                              <RichTextReader
                                customStyles={{
                                  div: {
                                    textAlign: "center",
                                  },
                                }}
                                html={q.label}
                              />
                            )}
                            <RichTextReader html={q.question} />
                          </Stack>
                        </Grid>
                        <Grid item xs={remainingColumns}>
                          <Stack direction="row" spacing="sm">
                            {options.map((option, index) => (
                              <div key={index} style={{ flex: 1, display: "flex", justifyContent: "center" }}>
                                {(applicationQuestion.formQuestionTypeId == FormQuestionTypes.Matrix ||
                                  applicationQuestion.formQuestionTypeId == FormQuestionTypes.MatrixGrouped) && (
                                  <FormControlLabel
                                    sx={{ margin: 0 }}
                                    value={option.formAnswerId}
                                    control={<Radio onClick={(e) => onHandleChange(e, q)} />}
                                    label=""
                                    disabled={isDisabled}
                                  />
                                )}
                                {applicationQuestion.formQuestionTypeId == FormQuestionTypes.MatrixWriteIn && (
                                  <FormControlLabel
                                    sx={{ margin: 0 }}
                                    disabled={isDisabled}
                                    value={option.formAnswerId}
                                    control={
                                      <Input
                                        multiline
                                        disabled={isDisabled}
                                        value={
                                          values.find(
                                            (v) =>
                                              v.formAnswerId == option.formAnswerId &&
                                              v.subQuestionId == q.subQuestionId
                                          )?.writeIn || ""
                                        }
                                        onChange={(e) => onHandleWriteInChange(e, q, option.formAnswerId)}
                                      />
                                    }
                                    label=""
                                  />
                                )}
                                {applicationQuestion.formQuestionTypeId == FormQuestionTypes.MatrixMultipleChoice && (
                                  <FormControlLabel
                                    sx={{ margin: 0 }}
                                    disabled={isDisabled}
                                    value={option.formAnswerId}
                                    control={
                                      <Checkbox
                                        checked={values.some(
                                          (v) =>
                                            v.formAnswerId == option.formAnswerId && v.subQuestionId == q.subQuestionId
                                        )}
                                        onChange={(e) => handleMultiChange(e, q)}
                                        name={option.label}
                                        disabled={isDisabled}
                                      />
                                    }
                                    label=""
                                  />
                                )}
                              </div>
                            ))}
                          </Stack>
                        </Grid>
                      </MuiRadioGroup>
                    );
                  })}
                </FormControl>
              </Grid>
            </Grid>
          </>
        )}
      </>
    );
  };

  const renderSurveyMobileView = () => {
    return (
      <>
        {formQuestion && (
          <FormControl style={{ width: "100%" }}>
            <Grid container spacing="lg">
              {subQuestions.map((q, i) => {
                return (
                  <>
                    <Grid item xs={12} pb={2} key={i}>
                      <Stack direction="row" spacing={2}>
                        {formQuestion.formQuestionTypeId == FormQuestionTypes.MatrixGrouped && (
                          <RichTextReader html={q.label} />
                        )}
                        <RichTextReader html={q.question} />
                      </Stack>
                    </Grid>

                    <Grid item xs={12}>
                      <MuiRadioGroup
                        sx={{
                          padding: 1,
                          background: i % 2 && formQuestion.isAlternatingRowColor ? "#f5f5f5" : "white",
                        }}
                        row
                        key={i}
                        aria-labelledby="radio-buttons-group"
                        name="controlled-radio-buttons-group"
                        value={getValue(q)}
                        // onClick={(e) => onHandleChange(e, q)}
                      >
                        <Grid container direction="column" spacing="sm">
                          {options.map((o, i) => {
                            return (
                              <Grid
                                key={i}
                                item
                                xs={mobileOptionSize}
                                style={{ display: "flex", justifyContent: "flex-start" }}
                              >
                                <Stack direction="row" alignItems="center">
                                  {(formQuestion.formQuestionTypeId == FormQuestionTypes.Matrix ||
                                    formQuestion.formQuestionTypeId == FormQuestionTypes.MatrixGrouped) && (
                                    <FormControlLabel
                                      value={o.formAnswerId}
                                      disabled={isDisabled}
                                      control={<Radio onClick={(e) => onHandleChange(e, q)} />}
                                      label=""
                                    />
                                  )}
                                  {formQuestion.formQuestionTypeId == FormQuestionTypes.MatrixWriteIn && (
                                    <FormControlLabel
                                      value={o.formAnswerId}
                                      disabled={isDisabled}
                                      control={
                                        <Input
                                          multiline
                                          value={
                                            values.find(
                                              (v) =>
                                                v.formAnswerId == o.formAnswerId && v.subQuestionId == q.subQuestionId
                                            )?.writeIn || ""
                                          }
                                          onChange={(e) => onHandleWriteInChange(e, q, o.formAnswerId)}
                                        />
                                      }
                                      label=""
                                    />
                                  )}
                                  {formQuestion.formQuestionTypeId == FormQuestionTypes.MatrixMultipleChoice && (
                                    <FormControlLabel
                                      value={o.formAnswerId}
                                      disabled={isDisabled}
                                      control={
                                        <Checkbox
                                          checked={values.some(
                                            (v) =>
                                              v.formAnswerId == o.formAnswerId && v.subQuestionId == q.subQuestionId
                                          )}
                                          onChange={(e) => handleMultiChange(e, q)}
                                          name={o.label}
                                          disabled={isDisabled}
                                        />
                                      }
                                      label=""
                                    />
                                  )}
                                  <RichTextReader html={o.label} />
                                </Stack>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </MuiRadioGroup>
                    </Grid>
                  </>
                );
              })}
            </Grid>
          </FormControl>
        )}
      </>
    );
  };

  const renderApplicationMobileView = () => {
    return (
      <>
        {applicationQuestion && (
          <FormControl style={{ width: "100%" }}>
            <Grid container spacing="lg">
              {subQuestions.map((q, i) => {
                return (
                  <>
                    <Grid item xs={12} pb={2} key={i}>
                      <Stack direction="row" spacing={2}>
                        {applicationQuestion.formQuestionTypeId == FormQuestionTypes.MatrixGrouped && (
                          <RichTextReader html={q.label} />
                        )}
                        <RichTextReader html={q.question} />
                      </Stack>
                    </Grid>

                    <Grid item xs={12}>
                      <MuiRadioGroup
                        sx={{ padding: 1, background: "white" }}
                        row
                        key={i}
                        aria-labelledby="radio-buttons-group"
                        name="controlled-radio-buttons-group"
                        value={getValue(q)}
                        // onClick={(e) => onHandleChange(e, q)}
                      >
                        <Grid container direction="column" spacing="sm">
                          {options.map((o, i) => {
                            return (
                              <Grid
                                key={i}
                                item
                                xs={mobileOptionSize}
                                style={{ display: "flex", justifyContent: "flex-start" }}
                              >
                                <Stack direction="row" alignItems="center">
                                  {(applicationQuestion.formQuestionTypeId == FormQuestionTypes.Matrix ||
                                    applicationQuestion.formQuestionTypeId == FormQuestionTypes.MatrixGrouped) && (
                                    <FormControlLabel
                                      value={o.formAnswerId}
                                      disabled={isDisabled}
                                      control={<Radio onClick={(e) => onHandleChange(e, q)} />}
                                      label=""
                                    />
                                  )}
                                  {applicationQuestion.formQuestionTypeId == FormQuestionTypes.MatrixWriteIn && (
                                    <FormControlLabel
                                      value={o.formAnswerId}
                                      disabled={isDisabled}
                                      control={
                                        <Input
                                          multiline
                                          value={
                                            values.find(
                                              (v) =>
                                                v.formAnswerId == o.formAnswerId && v.subQuestionId == q.subQuestionId
                                            )?.writeIn || ""
                                          }
                                          onChange={(e) => onHandleWriteInChange(e, q, o.formAnswerId)}
                                        />
                                      }
                                      label=""
                                    />
                                  )}
                                  {applicationQuestion.formQuestionTypeId == FormQuestionTypes.MatrixMultipleChoice && (
                                    <FormControlLabel
                                      value={o.formAnswerId}
                                      disabled={isDisabled}
                                      control={
                                        <Checkbox
                                          checked={values.some(
                                            (v) =>
                                              v.formAnswerId == o.formAnswerId && v.subQuestionId == q.subQuestionId
                                          )}
                                          onChange={(e) => handleMultiChange(e, q)}
                                          name={o.label}
                                          disabled={isDisabled}
                                        />
                                      }
                                      label=""
                                    />
                                  )}
                                  <RichTextReader html={o.label} />
                                </Stack>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </MuiRadioGroup>
                    </Grid>
                  </>
                );
              })}
            </Grid>
          </FormControl>
        )}
      </>
    );
  };

  return <>{isMobile ? formQuestion ? renderSurveyMobileView() : renderApplicationMobileView() : formQuestion ? renderSurveyDesktopView() : renderApplicationDesktopView()}</>;
};
